/**
 * 差分を段ごとに左右へ割って並べる器と、その切り替え。
 *
 * **AS-IS と TO-BE の区別は、色でも文言でもなく「位置」で決める。** 左が必ず変更前、右が必ず
 * 変更後である。同じセルの中に変更前を打ち消し線で重ねる形（`BeforeValue`）は、変更が数件なら
 * 読めるが、規則を書き換えた PJ では 1 件ごとに 2 行ずつ積み上がり、画面が文字の壁になる。
 * どちらが現在の仕様でどちらが PJ の結果かを、読み手が毎行たどって判断することになっていた。
 *
 * 見出しは**左右とも塗り、色で側を言う**。左は消えた側の赤、右は入った側の緑である。以前は右だけを
 * 強調色で塗っていたが、**中身のセルが左右で同じ色だった頃の名残**である。セルが赤と緑に分かれた今、
 * 見出しだけ片方が無色だと、色の意味を見出しで確かめられない。左右のどちらが変更前かは位置が決める
 * という規則は変わらない — 色はその位置に名前を付ける。
 *
 * 見出しの字は**全画面で「変更前」「変更後」の 2 語だけ**にし、「公開中の仕様」と PJ 番号は見出しを
 * 指すと出す（Dhik 2026-09-29）。画面ごとに添え書きの有無が違うと、同じ比較が画面で違う姿に見える。
 * PJ はモードの帯で読める。
 *
 * **1 段ぶんの左右は同じ行の中に入れる。** 左右を別々の一覧として縦に積むと、行数が同じでも
 * **段の高さが揃わない**。規則の本文は折り返す長さが左右で違うので、1 段目で数ピクセル
 * ずれた分が下へ累積し、3 段目には別の規則が隣り合って見える。読み手は毎段「これは同じ規則の
 * 変更前か」を ID で確かめることになり、位置で示すという仕組みそのものが働かない。
 * 対を 1 つの `<li>`（2 列のグリッド）に収めれば、段の高さは**背の高いほうに揃う**のが
 * ブラウザの既定になり、ずれが起きえない形になる。
 *
 * 行の突き合わせ自体は呼び出し側の責任である（`mergeById`）。突き合わせ済みの 1 本の並びを
 * 対の列として渡し、その行がその側に存在しないときだけ空札（`DiffAbsentCell`）を置く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分を変更前・変更後の対で 1 行ずつ並べる器と、1 枚表示との切り替えを提供する。
 * @graph-connects none
 */
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { formatPjLabel } from '../../lib/pj-label.js';
import { StateToggleButton } from './StateToggleButton.js';

/**
 * 左右比較のどちら側か。
 *
 * 器（対の一覧）を使う画面と、1 枚の表を左右のカラム対にする表の両方で使う。**どちらも
 * 「同じ段の左右を同じ行の中に置く」形に揃えてある** — 一覧は 1 つの `<li>` の中の 2 列、
 * 表は 1 つの `<tr>` の中の 2 組の列である。左右を別々の入れ物として並べる形は取らない。
 * 折り返しが左右で違うと同じ段が違う高さに出て、行が揃わなくなるためである。
 */
export type DiffSide = 'before' | 'after';

/** `DiffCompareToggle` の props。 */
export interface DiffCompareToggleProps {
  /** いま左右に並べているか。 */
  split: boolean;
  /** 切り替え。 */
  onToggle: () => void;
  /** 変更前と変更後に差が無く、比べても同じものが 2 つ並ぶだけか。省けば押せる。 */
  disabled?: boolean;
  /** ボタンを指したときに出す断り（1 枚の表示に出せない削除の件数など）。省けば出さない。 */
  title?: string;
}

/**
 * 1 枚表示と左右比較を切り替えるボタン。
 *
 * **文言はフロー図と共有する。** 同じ操作が画面ごとに違う名前で出ると、読み手は画面を移る
 * たびに「これはさっきの比較と同じものか」を確かめることになる。差分の語彙は
 * `SpecDiffState` と同じく 1 箇所に置く。
 *
 * **切り替えは 1 つのボタンの押し下げで表す**（PJ から見たフロー図が先に決めた形）。「1 枚」
 * 「左右比較」を別々のボタンに割ると、既定が 1 枚であることが並んだ 2 択に埋もれ、比較が
 * 常設の表示に見える。
 *
 * **文言は押すと起きることを書き、押し下げで入れ替える**（Dhik 2026-09-29「押したときに起きる
 * ことをラベルに書いて切り替えてほしい」）。1 枚のときは「変更前と比較する」、左右比較のときは
 * 「PJ リリース後だけ見る」。塗りと `aria-pressed` だけでは、比較を閉じると PJ リリース後の
 * 1 枚に戻ることが読めなかった。形は `StateToggleButton` が持つ。
 *
 * **差が無ければ押せなくする**（2026-09-29 に決めた）。同じ姿を左右に並べても読み手が得るものは
 * 無く、「どこが違うのか」を探させるだけになる。ボタンを消さずに押せない形で残すのは、案件モードの
 * 画面ごとにボタンの有無が変わると、比較できない理由（差が無い）が読めなくなるためである。
 * 押せないときは押し下げも外し、文言は「変更前と比較する」のままにする。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 1 枚表示と左右比較の切り替えボタンを、全 diff 画面で押すと起きることを書いた同じ文言と同じ押し下げで描き、差が無ければ押せなくし、断りが渡れば指したときに出す。
 * @graph-connects StateToggleButton [calls] 押し下げで文言を入れ替えるボタン
 */
export function DiffCompareToggle({
  split,
  onToggle,
  disabled = false,
  title,
}: DiffCompareToggleProps) {
  const { t } = useTranslation();

  return (
    <StateToggleButton
      pressed={split}
      idleLabel={t('specDiff.compareToggle')}
      pressedLabel={t('specDiff.compareToggleActive')}
      onToggle={onToggle}
      disabled={disabled}
      {...(title === undefined ? {} : { title })}
    />
  );
}

/** 左右 1 対で 1 段ぶん。 */
export interface DiffSplitRow {
  /**
   * 段の同一性。左右の対を同じ `<li>` に収めるための鍵で、React の再利用の鍵にもなる。
   *
   * 並びの添字ではなく対象そのものの ID を渡す。添字を鍵にすると、PJ が段を 1 つ落としたとき
   * に以降の段が別の対象の状態を引き継ぎ、開いていた段や打ち消しが 1 段ずれて残る。
   */
  key: string;
  /** その段の変更前。その側に存在しなければ空札を渡す。 */
  before: ReactNode;
  /** その段の変更後。その側に存在しなければ空札を渡す。 */
  after: ReactNode;
}

/** `DiffSplitPanes` の props。 */
export interface DiffSplitPanesProps {
  /**
   * 右の見出しを指すと出す PJ 番号。PJ を選んでいなければ `null`。
   *
   * 空文字ではなく `null` にするのは、PJ を選んでいないことと PJ 番号が空文字であることを
   * 描く側が区別できるようにするためである（前者なら番号を出さない）。
   */
  pjNumber: string | null;
  /** 突き合わせ済みの段を、上から順に対で渡す。 */
  rows: readonly DiffSplitRow[];
}

/**
 * 段ごとの左右を、対のまま上から並べる。
 *
 * 見出しは `sticky` にする。規則や手順は縦に伸びるので、下まで読んだところで「いま見ている
 * のは左か右か」が画面から消えると、位置で区別する仕組みそのものが働かなくなる。
 *
 * 見出しと段は**同じ列の定め**（1 列、中画面以上で 2 列）を使う。定めを 2 箇所に書くと、
 * 片方だけ変えたときに見出しと中身の列がずれる。
 *
 * 各段のセルは `grid` にしてある。段の高さは背の高い側で決まるので、低い側のセルは余白を
 * 抱える。中身を上端に置いたままだと枠が中途半端な高さで切れて、対の関係が視覚的に切れる。
 * `grid` なら中の 1 枚がセルの高さまで伸び、**左右の枠が同じ高さで閉じる**。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の段ごとの変更前・変更後を同じ行に対で並べ、左右の高さを揃えて描く。
 * @graph-connects none
 */
export function DiffSplitPanes({ pjNumber, rows }: DiffSplitPanesProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const beforeHeadingId = `${headingId}-before`;
  const afterHeadingId = `${headingId}-after`;
  const columns = 'grid grid-cols-1 gap-3 md:grid-cols-2';

  return (
    <div className="flex flex-col gap-2">
      <div className={`sticky top-0 z-10 ${columns}`}>
        <h3
          id={beforeHeadingId}
          title={t('specDiff.paneBeforeNote')}
          className="min-w-0 bg-[var(--color-diff-removed-pane)] px-2 py-1.5 text-xs font-bold text-[var(--color-bg-base)]"
        >
          {t('specDiff.paneBefore')}
        </h3>
        <h3
          id={afterHeadingId}
          {...(pjNumber === null ? {} : { title: formatPjLabel(pjNumber) })}
          className="min-w-0 bg-[var(--color-diff-added-pane)] px-2 py-1.5 text-xs font-bold text-[var(--color-bg-base)]"
        >
          {t('specDiff.paneAfter')}
        </h3>
      </div>
      <ol className="flex flex-col gap-3">
        {rows.map((row) => (
          <li key={row.key} className={columns}>
            <div
              role="group"
              aria-labelledby={beforeHeadingId}
              data-diff-side="before"
              className="grid min-w-0"
            >
              {row.before}
            </div>
            <div
              role="group"
              aria-labelledby={afterHeadingId}
              data-diff-side="after"
              className="grid min-w-0"
            >
              {row.after}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * 片側に存在しない行の空札。
 *
 * 追加された行は変更前に、削除された行は変更後に、置く中身が無い。詰めると以降の行が 1 つずつ
 * ずれ、左右で別のものが並ぶ。**「無い」を占める箱を置いて位置を保つ。** 何も置かずに高さだけ
 * 空けると、読み手には描画の失敗と区別が付かないので、無いことを文字で言う。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 片側に存在しない行の位置を、無いことを明示した空札で占める。
 * @graph-connects none
 */
export function DiffAbsentCell({ state }: { state: 'created' | 'deleted' }) {
  const { t } = useTranslation();

  return (
    <div
      data-absent={state}
      className="flex items-center border border-dashed border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-text-muted)]"
    >
      {state === 'created' ? t('specDiff.absentBefore') : t('specDiff.absentAfter')}
    </div>
  );
}
